import { EApplicantType, ECreditApplicationStatus, EDecision, EFinancingProgram } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { History as HistoryIcon, Info as InfoIcon, LocalPrintshop as LocalPrintshopIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import {
  useIsCreditRslaHardHitRequired,
  useIsOnlyPrequalificationDone,
  useIsRefusedWithNoCreditReport,
  useMerchantPaymentPlan,
} from '@src/data/selectors/credit-selectors'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCurrentLang, usePlanEnumForProgram } from '../../../data/selectors/app-config-selectors'
import { Constants, PersonalLoanWorksheet, User } from '../../../data/types'
import { CreditApplication } from '../../../data/types/CreditApplicationSchema'
import CreditSectionInformation from './creditSectionInformation'
import FormattedMerchantPaymentPlan from './FormattedMerchantPaymentPlan'

interface Props {
  creditApplication: CreditApplication
  handleEditBankruptciesClicked: (data: EApplicantType) => void
  editDisabled: boolean
  user: User | null
  onDisplayHistory: () => void
}

const CreditSection = ({
  creditApplication,
  handleEditBankruptciesClicked,
  editDisabled,
  user,
  onDisplayHistory,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { worksheet } = useCreditApplicationData()
  const personalLoanWorksheet = worksheet as PersonalLoanWorksheet | undefined

  const planEnum = usePlanEnumForProgram(creditApplication.financingProgramId)
  const lang = useCurrentLang()
  const merchantPaymentPlan = useMerchantPaymentPlan(creditApplication, personalLoanWorksheet)

  const isOnlyPrequalificationDone = useIsOnlyPrequalificationDone()
  const isCreditRslaHardHitRequired = useIsCreditRslaHardHitRequired()

  let decisionBorderColor = 'warning.main'

  const isRefusedWithNoCreditReport = useIsRefusedWithNoCreditReport()

  if (
    creditApplication.status === ECreditApplicationStatus.Cancelled ||
    creditApplication.status === ECreditApplicationStatus.Expired
  )
    decisionBorderColor = 'warning.main'
  else if (creditApplication.finalCreditDecision.decision === EDecision.Refused) decisionBorderColor = 'error.main'
  else if (creditApplication.finalCreditDecision.decision === EDecision.Approved) decisionBorderColor = 'success.main'

  const applicationIsDraft = creditApplication.status === ECreditApplicationStatus.Draft

  const messageDecision = () => {
    if (applicationIsDraft) return t('ECreditApplicationStatus.draft', { ns: 'enum' })

    if (
      creditApplication.status === ECreditApplicationStatus.Cancelled ||
      creditApplication.status === ECreditApplicationStatus.Expired
    )
      return 'N/A'

    if (
      creditApplication.financingProgramId === EFinancingProgram.CreditRsla &&
      !isCreditRslaHardHitRequired &&
      creditApplication.prequalificationDecision?.decision
    ) {
      return t(`EDecision.${creditApplication.prequalificationDecision.decision}`, { ns: 'enum' })
    }

    if (isRefusedWithNoCreditReport) {
      return t('common.refusedWithNoCreditReport')
    }
    if (creditApplication.agentCreditDecision?.overrideReason) {
      return t('credit.overriden')
    }

    return t(`EDecision.${creditApplication.finalCreditDecision.decision}`, { ns: 'enum' })
  }

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mt: 3, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <ButtonGroup variant="outlined" size="large">
            <Button
              id="btnViewCreditDecision"
              disabled={
                applicationIsDraft ||
                isOnlyPrequalificationDone ||
                !Constants.financingProgramConfigs[creditApplication.financingProgramId].useCreditSection ||
                (!creditApplication.prequalificationDecision?.decision &&
                  !creditApplication.finalCreditDecision?.decision) ||
                (creditApplication.financingProgramId === EFinancingProgram.CreditRsla && !isCreditRslaHardHitRequired)
              }
              onClick={() =>
                navigate(`/Applications/${creditApplication.financingProgramId}/${creditApplication.id}/decision`)
              }
            >
              {t('credit.credit')}
            </Button>
            <Button disabled={applicationIsDraft || editDisabled || isOnlyPrequalificationDone} size="small">
              <LocalPrintshopIcon fontSize="large" />
            </Button>
          </ButtonGroup>
          <Typography>
            {t('common.LastModification')} : {new Date(creditApplication.updatedOn).toLocaleDateString()} |{' '}
            {new Date(creditApplication.updatedOn).toLocaleTimeString()}
          </Typography>
        </Stack>
      </Box>
      <Divider variant="middle" />
      <Box sx={{ m: 1 }}>
        {isOnlyPrequalificationDone ? (
          <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 0.5, mt: 2, mb: 1 }}>
            <Typography
              variant="body1"
              component="div"
              sx={{ border: '2px solid', borderRadius: 2, p: 0.5, borderColor: decisionBorderColor, mr: 1 }}
            >
              Prequalification:{' '}
              {isRefusedWithNoCreditReport
                ? t('common.refusedWithNoCreditReport')
                : t(`EDecision.${creditApplication.prequalificationDecision?.decision}`, { ns: 'enum' })}
            </Typography>
            {merchantPaymentPlan ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <Typography sx={{ mr: 1 }}>Promo:</Typography>
                <FormattedMerchantPaymentPlan merchantPaymentPlan={merchantPaymentPlan} />
              </Box>
            ) : (
              <Typography variant="body1" component="div" sx={{ mr: 1 }}>
                {t('credit.interestRate')} : {creditApplication.prequalificationDecision?.minInterestRate}% -{' '}
                {creditApplication.prequalificationDecision?.maxInterestRate}%
              </Typography>
            )}
            <Typography variant="body1" component="div" sx={{ mr: 1 }}>
              {t('editCreditApplication.maximumLoanAmount')} :{' '}
              {formatCurrency(creditApplication.prequalificationDecision?.maxLoanAmount)}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              columnGap: 0.5,
              mt: 2,
              mb: 1,
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <Typography
                variant="body1"
                component="div"
                sx={{ border: '2px solid', borderRadius: 2, p: 0.5, borderColor: decisionBorderColor }}
              >
                {messageDecision()}
              </Typography>
              {creditApplication.agentCreditDecision?.overrideReason && (
                <Tooltip title={creditApplication.agentCreditDecision.overrideReason}>
                  <InfoIcon sx={{ ml: 0.5, color: 'warning.main', cursor: 'pointer' }} />
                </Tooltip>
              )}
            </Box>
            {creditApplication.financingProgramId === EFinancingProgram.CreditRsla &&
              !isCreditRslaHardHitRequired &&
              creditApplication.prequalificationDecision?.decision !== EDecision.Pending &&
              creditApplication.prequalificationDecision?.decision !== EDecision.Refused && (
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    border: '2px solid',
                    borderRadius: 2,
                    p: 0.5,
                    borderColor: decisionBorderColor,
                  }}
                >
                  {t('credit.noHardHitRequired')}
                </Typography>
              )}
            {merchantPaymentPlan ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <Typography sx={{ mr: 1 }}>Promo:</Typography>
                <FormattedMerchantPaymentPlan merchantPaymentPlan={merchantPaymentPlan} />
              </Box>
            ) : (
              <Typography variant="body1" component="div" sx={{ mr: 1 }}>
                {t('credit.interestRate')} : {creditApplication.finalCreditDecision.interestRate}%
              </Typography>
            )}

            <Typography variant="body1" component="div" sx={{ mr: 1 }}>
              {t('credit.plan')} :{' '}
              {creditApplication.normsCreditDecision?.planName != null && planEnum != null
                ? planEnum[creditApplication.normsCreditDecision?.planName ?? ''][lang]
                : 'N/A'}
            </Typography>

            {creditApplication.financingProgramId === EFinancingProgram.Auto && (
              <Typography variant="body1" component="div" sx={{ mr: 1 }}>
                {t('credit.cash')} : {creditApplication.decisionOverrides?.cashdownPercent}
                {creditApplication.decisionOverrides?.cashdownPercent !== undefined && '%'}
              </Typography>
            )}
            {creditApplication.financingProgramId !== EFinancingProgram.CreditRsla && (
              <Typography variant="body1" component="div" sx={{ mr: 1 }}>
                {t('credit.maxPayment')} : {formatCurrency(creditApplication.finalCreditDecision.maxPmtAmount)}
              </Typography>
            )}
            <IconButton onClick={onDisplayHistory}>
              <HistoryIcon fontSize="large" color="primary" />
            </IconButton>
            {creditApplication.financingProgramId === EFinancingProgram.Auto && (
              <span>
                <Typography variant="body1" component="div">
                  {t('credit.refinancing')} : {formatCurrency(creditApplication.refinancing?.amountToRefinance)}
                </Typography>

                <Typography variant="body1" component="div">
                  {t('credit.commitment')} : {formatCurrency(creditApplication.refinancing?.engagementAmount)}
                </Typography>
              </span>
            )}
          </Box>
        )}
      </Box>
      <Grid container justifyContent="space-between">
        <Grid
          size={{
            xs: 12,
            md: 12,
            lg: 6,
          }}
        >
          <CreditSectionInformation
            applicant={creditApplication.applicant}
            creditApplicationId={creditApplication.id}
            financingProgramId={creditApplication.financingProgramId}
            currentApplicant="applicant"
            handleEditBankruptciesClicked={handleEditBankruptciesClicked}
            editDisabled={editDisabled}
            userCanEditCreditApplication={user?.rights.canEditCreditApp === true}
            applicationIsDraft={applicationIsDraft}
            finalDecision={creditApplication.finalCreditDecision}
            prequalificationDecision={creditApplication.prequalificationDecision}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 12,
            lg: 6,
          }}
        >
          {creditApplication.coApplicant !== null && (
            <CreditSectionInformation
              applicant={creditApplication.coApplicant}
              creditApplicationId={creditApplication.id}
              financingProgramId={creditApplication.financingProgramId}
              currentApplicant="coApplicant"
              handleEditBankruptciesClicked={handleEditBankruptciesClicked}
              editDisabled={editDisabled}
              userCanEditCreditApplication={user?.rights.canEditCreditApp === true}
              applicationIsDraft={applicationIsDraft}
              finalDecision={creditApplication.finalCreditDecision}
              prequalificationDecision={creditApplication.prequalificationDecision}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  )
}

export default memo(CreditSection)
